import { createStore } from "@/features/tutor/store/createStore";

export type WriteMode = "read" | "edit";
export type CollabMode = "local" | "shared";
export type AiMode = "off" | "text-only" | "voice";
export type ParticipantStatus = "working" | "present" | "idle" | "just_joined";
// Backend-driven space lifecycle. Frontend-only states (e.g. "loading" before
// the join request resolves) live as local unions in the page that owns them.
export type SpaceStatus = "draft" | "open" | "paused" | "closed";

export type ParticipantClassroom = {
  id: number;
  name: string;
};

export type SpaceParticipant = {
  userId: number;
  name: string;
  writeMode: WriteMode;
  joinedAt?: Date;
  lastJoinedAt?: Date;
  timeSpentMs?: number;
  status?: ParticipantStatus;
  // Classrooms (out of the space's classrooms) the student is enrolled in.
  // Already sorted by name on the backend. Empty array → student is not in
  // any of the space's classrooms (orphaned StudentSpaceSession row).
  classrooms?: ParticipantClassroom[];
};

type SpaceState = {
  spaceId: string | null;
  spaceName: string | null;
  lessonContext: string | null;
  aiMode: AiMode;
  collabMode: CollabMode;
  participants: SpaceParticipant[];
  // True once participants have been loaded from the API at least once.
  // Used to distinguish "not yet loaded" from "genuinely zero participants"
  // so the UI can show a skeleton during the initial fetch.
  participantsLoaded: boolean;
  connectedUserIds: Set<number>;
  startedAt: Date | null;
  totalEnrolled: number;
  setSpace: (
    spaceId: string,
    spaceName: string,
    lessonContext: string | null,
  ) => void;
  clearSpace: () => void;
  setAiMode: (mode: AiMode) => void;
  setCollabMode: (mode: CollabMode) => void;
  setParticipants: (participants: SpaceParticipant[]) => void;
  setConnectedUserIds: (ids: number[]) => void;
  addConnected: (userId: number) => void;
  removeConnected: (userId: number) => void;
  updateParticipantWriteMode: (userId: number, writeMode: WriteMode) => void;
  removeParticipant: (userId: number) => void;
  setStartedAt: (d: Date | null) => void;
  setTotalEnrolled: (n: number) => void;
  updateParticipantStatus: (userId: number, status: ParticipantStatus) => void;
  updateParticipantJoinedAt: (userId: number, joinedAt: Date) => void;
};

export const useSpaceStore = createStore<SpaceState>("SpaceStore", (set) => ({
  spaceId: null,
  spaceName: null,
  lessonContext: null,
  aiMode: "off",
  collabMode: "local",
  participants: [],
  participantsLoaded: false,
  connectedUserIds: new Set(),
  startedAt: null,
  totalEnrolled: 0,
  setSpace: (spaceId, spaceName, lessonContext) =>
    set({ spaceId, spaceName, lessonContext }),
  clearSpace: () =>
    set({
      spaceId: null,
      spaceName: null,
      lessonContext: null,
      aiMode: "off",
      collabMode: "local",
      participants: [],
      participantsLoaded: false,
      connectedUserIds: new Set(),
      startedAt: null,
      totalEnrolled: 0,
    }),
  setAiMode: (aiMode) => set({ aiMode }),
  setCollabMode: (collabMode) => set({ collabMode }),
  setParticipants: (incoming) =>
    set((s) => {
      const existingMap = new Map(s.participants.map((p) => [p.userId, p]));
      return {
        participants: incoming.map((p) => ({
          ...existingMap.get(p.userId),
          ...p,
        })),
        participantsLoaded: true,
      };
    }),
  setConnectedUserIds: (ids) => set({ connectedUserIds: new Set(ids) }),
  addConnected: (userId) =>
    set((s) => ({
      connectedUserIds: new Set([...s.connectedUserIds, userId]),
    })),
  removeConnected: (userId) =>
    set((s) => {
      const next = new Set(s.connectedUserIds);
      next.delete(userId);
      return { connectedUserIds: next };
    }),
  updateParticipantWriteMode: (userId, writeMode) =>
    set((s) => ({
      participants: s.participants.map((p) =>
        p.userId === userId ? { ...p, writeMode } : p,
      ),
    })),
  removeParticipant: (userId) =>
    set((s) => ({
      participants: s.participants.filter((p) => p.userId !== userId),
    })),
  setStartedAt: (startedAt) => set({ startedAt }),
  setTotalEnrolled: (totalEnrolled) => set({ totalEnrolled }),
  updateParticipantStatus: (userId, status) =>
    set((s) => ({
      participants: s.participants.map((p) =>
        p.userId === userId ? { ...p, status } : p,
      ),
    })),
  updateParticipantJoinedAt: (userId, joinedAt) =>
    set((s) => ({
      participants: s.participants.map((p) =>
        p.userId === userId ? { ...p, joinedAt } : p,
      ),
    })),
}));
